import { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { FiX } from "react-icons/fi";
import { LuLandmark } from "react-icons/lu";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    borderRadius: "16px",
    padding: 0,
    border: "none",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    width: "440px",
    maxWidth: "92vw",
  },
  overlay: {
    background: "rgba(15, 23, 42, 0.55)",
    backdropFilter: "blur(2px)",
    zIndex: 9999,
  },
};

const MAX_LENGTH = 100;

/* Add (bank = null) or rename a bank */
export default function BankFormModal({ isOpen, bank, existingNames = [], isSaving, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const isEdit = !!bank;

  useEffect(() => {
    if (isOpen) {
      setName(bank?.name || "");
      setError("");
    }
  }, [isOpen, bank]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim().replace(/\s+/g, " ");
    if (!trimmed) return setError("Emri i bankës është i detyrueshëm!");
    if (trimmed.length > MAX_LENGTH) return setError(`Emri mund të ketë maksimum ${MAX_LENGTH} karaktere!`);
    const duplicate = existingNames.some(
      (n) => n.toLowerCase() === trimmed.toLowerCase() && n.toLowerCase() !== (bank?.name || "").toLowerCase()
    );
    if (duplicate) return setError("Një bankë me këtë emër ekziston tashmë!");
    if (isEdit && trimmed === bank.name) return onClose();
    onSubmit(trimmed);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isSaving ? undefined : onClose}
      onAfterOpen={() => inputRef.current?.focus()}
      style={customStyles}
      contentLabel={isEdit ? "Ndrysho bankën" : "Shto bankë"}
    >
      <form onSubmit={handleSubmit} className="flex flex-col">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[var(--secondary-color)]/10 text-[var(--secondary-color)]">
            <LuLandmark size={18} />
          </span>
          <h2 className="flex-1 text-base font-bold text-gray-900">{isEdit ? "Ndrysho bankën" : "Shto bankë"}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Mbyll"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:bg-gray-100 transition"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-1.5">
          <label htmlFor="bank-name" className="text-xs font-semibold text-gray-700">
            Emri i bankës
          </label>
          <input
            id="bank-name"
            ref={inputRef}
            type="text"
            value={name}
            maxLength={MAX_LENGTH + 20}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError("");
            }}
            placeholder="p.sh. Banka Ekonomike"
            className={`w-full h-11 rounded-lg border px-3 text-sm shadow-sm transition focus:outline-none focus:ring-2 ${
              error
                ? "border-red-400 focus:ring-red-200"
                : "border-gray-300 focus:ring-[var(--secondary-color)]/30 focus:border-[var(--secondary-color)]"
            }`}
          />
          {error && <div className="text-xs font-medium text-red-600">{error}</div>}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
          >
            Anulo
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="rounded-lg bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:opacity-60"
          >
            {isSaving ? "Duke ruajtur..." : isEdit ? "Ruaj ndryshimet" : "Shto bankën"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
